/**
 * LA CARTE DES ONGLETS (DEV-1508 lots 2 et 3, engagements E1 et E9).
 * ============================================================================
 *
 * INTENTION : « pour tout compteur Enedis, la page montre TOUT ce que le lecteur
 * sert pour CE compteur, rangé dans les cinq catégories que la spec décide, sans
 * onglet vide » (E1, spec validée le 09/09).
 *
 * CE QUI CHANGE AU LOT 3, et pourquoi. Les onglets se déduisaient d'une carte
 * fixe PAR SEGMENT : un C2 portait le réactif inductif et capacitif parce que
 * « les segments fins publient le réactif ». Deux défauts, tous deux servis au
 * client :
 *
 *  • des séries demandées sous des noms que le lecteur ne sert PAS pour ce
 *    compteur (les composantes réactives ne se demandent jamais séparément :
 *    seul le nom de commodité `puissancereactive_elec<sens>_total` existe côté
 *    lecteur) reviennent en colonnes de `null`, indistinguables d'un compteur
 *    non collecté ;
 *  • des grandeurs réellement servies n'apparaissaient NULLE PART (l'énergie
 *    réactive horaire, par exemple), donc invisibles pour le client : une donnée
 *    juste mais cachée est une donnée perdue.
 *
 * Depuis DEV-1768, `/source/informations` sert `data.<sens>.variables_datav2` :
 * la liste EXACTE des variables dataV2 servies pour ce compteur, avec leurs
 * statistiques et les grains où de la donnée est détenue, avec leurs bornes.
 * Cette liste, et elle seule, décide désormais des onglets et des séries. Le
 * segment ne décide plus rien : il n'était qu'une approximation de ce fait.
 *
 * Quatre règles qui tiennent tout :
 *
 * 1. AUCUN ONGLET VIDE. Un onglet sans série n'est pas rendu grisé, il n'est pas
 *    rendu du tout.
 *
 * 2. RIEN NE SE FABRIQUE. Une série n'existe que sur un nom que le compteur
 *    sert. Sans la liste, la page ouvre zéro onglet plutôt que cinq onglets
 *    plausibles.
 *
 * 3. L'UNITÉ NE SE DÉDUIT PAS DU NOM. `puissanceactive_*_tout.max` au jour vaut
 *    des kVA quand c'est la puissance maximale relevée, des kW quand c'est
 *    l'enveloppe dérivée des moyennes : deux grandeurs sous un même nom. Ces
 *    séries portent `unite: null` et l'écran lit `meta.unites` et `meta.natures`.
 *
 * 4. AUCUN NOM DE VARIABLE À L'ÉCRAN. Les libellés viennent d'une table de
 *    correspondance (grandeur + classe temporelle), jamais du nom servi : un
 *    client n'a pas à lire notre vocabulaire de stockage (E9, garde
 *    `vocabulaireEnedis.spec.ts`).
 */
import type {
    DirectionEnedis,
    GrainSeries,
    StatSeries,
} from "@modules/data/services/sourceDataEnedis.service"
import type { BornesVariableDataV2, VariableDataV2Servie } from "@types"

// ============================================================================
// SEGMENTS ET DIRECTIONS
// ============================================================================

/**
 * Segments servis par le référentiel du point. Le segment ne décide plus des
 * onglets (ce sont les variables servies qui le font) ; il reste un fait de
 * l'en-tête et le type que la page manipule.
 */
export type SegmentEnedis =
    | "C1"
    | "C2"
    | "C4"
    | "C5"
    | "P1"
    | "P3"
    | "P4"
    | "INDET"

/** Libellé client d'une direction. */
export const LIBELLES_DIRECTION: Record<DirectionEnedis, string> = {
    soutirage: "Soutirage",
    injection: "Injection",
}

/** L'ordre d'affichage des sens : le soutirage d'abord, partout. */
export const DIRECTIONS: DirectionEnedis[] = ["soutirage", "injection"]

// ============================================================================
// LES CINQ ONGLETS
// ============================================================================

export type CleOnglet =
    | "energie"
    | "puissance"
    | "depassements"
    | "index"
    | "facturation"

/** L'ordre est celui de la spec : Énergie d'abord, elle est ouverte par défaut. */
export const ORDRE_ONGLETS: CleOnglet[] = [
    "energie",
    "puissance",
    "depassements",
    "index",
    "facturation",
]

/**
 * CE QUE LE RAPPEL DE COMPLÉTUDE COMPTE, SOUS CHAQUE ONGLET (lot 5d, écart G1).
 *
 * Le rappel nomme la grandeur dont il compte les jours, pas l'onglet : « Énergie
 * journalière : 20 / 92 jours mesurés · 72 Enedis ». La FACTURATION n'en a pas —
 * une période de facturation n'est pas un jour, et compter ses jours mesurés
 * n'aurait aucun sens (`null` = rien ne s'écrit, conforme à la maquette).
 */
export const LIBELLES_RAPPEL_ONGLET: Record<CleOnglet, string | null> = {
    energie: "Énergie journalière",
    puissance: "Courbe et PMax",
    depassements: "Dépassements",
    index: "Index",
    facturation: null,
}

export const LIBELLES_ONGLETS: Record<CleOnglet, string> = {
    energie: "Énergie",
    puissance: "Puissance",
    depassements: "Dépassements",
    index: "Index",
    facturation: "Facturation",
}

/**
 * LES MÊMES ONGLETS, DITS EN INJECTION (lot 5g).
 *
 * INTENTION : sur un producteur pur, le mot « soutirage » ne doit apparaître
 * NULLE PART — il n'y a rien à soutirer sur ce point, et un onglet « Énergie »
 * muet sur le sens laisse croire à une consommation. La maquette écrit
 * « Énergie injectée », « Puissance (injection) », « Index (injection) » : ce
 * sont ses mots, repris tels quels.
 *
 * Ces libellés ne servent QUE lorsque toutes les séries de l'onglet sont en
 * injection. Dès qu'un onglet porte les deux sens, il reprend le libellé neutre
 * et ce sont les titres des graphes qui nomment le sens.
 */
export const LIBELLES_ONGLETS_INJECTION: Record<CleOnglet, string> = {
    energie: "Énergie injectée",
    puissance: "Puissance (injection)",
    depassements: "Dépassements (injection)",
    index: "Index (injection)",
    facturation: "Facturation (injection)",
}

export const LIBELLES_RAPPEL_ONGLET_INJECTION: Record<
    CleOnglet,
    string | null
> = {
    energie: "Énergie injectée",
    puissance: "Courbe d'injection",
    depassements: "Dépassements",
    index: "Index",
    facturation: null,
}

/**
 * D'où vient la valeur, en quatre mots et pas un de plus (décision de Mathieu du
 * 11/09/2026) :
 *
 *  • `mesure` — le compteur l'a mesurée ;
 *  • `calcul` — ce n'est pas une mesure du compteur, mais un calcul CERTAIN,
 *    fait sur tous les pas qu'il devait agréger ;
 *  • `estimation` — c'est un calcul dont la couverture est incomplète, ou une
 *    extrapolation : le nombre est plausible, il n'est pas établi ;
 *  • `correction` — la valeur a été corrigée après coup par le distributeur.
 *
 * Ce que la carte DÉCLARE pour une série ne vaut que par défaut : l'origine
 * réelle se lit point par point dans la réponse (`utils/origineEnedis.ts`).
 */
export type OrigineSerie = "mesure" | "calcul" | "estimation" | "correction"

export const LIBELLES_ORIGINE: Record<OrigineSerie, string> = {
    mesure: "Mesuré",
    calcul: "Calculé",
    estimation: "Estimé",
    correction: "Corrigé",
}

/** Une série à tracer : un nom dataV2, une direction, ses statistiques. */
export type SerieOnglet = {
    /** Clé stable, utilisée par la mémoire et par le suivi. */
    cle: string
    /** Libellé client. Jamais le nom de la variable. */
    libelle: string
    /**
     * La GRANDEUR seule, sans la classe temporelle : « Durée de dépassement »,
     * « Énergie facturée », « Index ». Quand une carte porte plusieurs classes
     * de la même famille, son titre nomme la famille une fois et laisse les
     * classes à la légende, plutôt que de répéter cinq fois la grandeur (G10,
     * G11). Identique à `libelle` sur une série sans classe.
     */
    libelleFamille: string
    /** Nom dataV2, direction et classe déjà substituées. */
    variable: string
    direction: DirectionEnedis
    stats: StatSeries[]
    /**
     * Unité attendue, ou `null` quand elle dépend de la SOURCE de la valeur et
     * doit être lue dans `meta.unites`.
     */
    unite: string | null
    origine: OrigineSerie
    /** Grains où ce compteur DÉTIENT cette donnée, servis par la route. */
    grains: GrainSeries[]
    /**
     * LE CODE CANONIQUE DE LA CLASSE TEMPORELLE (lot 5d4), ou `null` quand le
     * nom n'en porte aucune. Deux séries qui le partagent nomment la MÊME
     * classe sous deux codes servis, et se fondent en une ligne à l'affichage.
     */
    classe: string | null
    /**
     * LES BORNES SERVIES PAR GRAIN, recopiées telles quelles (lot 5d4).
     *
     * C'est d'elles, et d'elles seules, que la ligne de fraîcheur de la carte
     * tire sa date : l'agrégat du compteur (`data_retrieval.status`) ne parle
     * que du flux principal, et le recopier sur les dépassements écrivait « à
     * jour » sur une famille figée depuis huit mois.
     */
    bornes: Partial<Record<GrainSeries, BornesVariableDataV2>>
}

export type OngletResolu = {
    cle: CleOnglet
    libelle: string
    /**
     * Ce que le rappel de complétude compte, dit avec le sens de l'onglet quand
     * il n'en porte qu'un seul et que ce n'est pas le soutirage (lot 5g).
     * `null` sur la facturation : une période close n'est pas un jour.
     */
    libelleRappel: string | null
    /** Les sens portés par les séries de cet onglet, sans doublon (lot 5g). */
    directions: DirectionEnedis[]
    series: SerieOnglet[]
    /** Les grains détenus par au moins une série de l'onglet, sans doublon. */
    grains: GrainSeries[]
}

// ============================================================================
// LE VOCABULAIRE MONTRÉ AU CLIENT
// ============================================================================

/**
 * Les classes temporelles, telles qu'elles se DISENT. Les codes (`HPH`, `HCE`,
 * `BASE`) sont le vocabulaire du distributeur ; ce qui s'écrit à l'écran est la
 * phrase qu'ils signifient.
 *
 * Le vocabulaire est OUVERT : une classe inédite ne se perd pas, elle s'écrit
 * avec son code (« classe BCHC ») plutôt que de faire disparaître une donnée que
 * le compteur sert vraiment. Un dictionnaire exact en oublierait forcément.
 */
export const LIBELLES_CLASSE: Record<string, string> = {
    BASE: "toutes heures",
    HP: "heures pleines",
    HC: "heures creuses",
    HPH: "heures pleines saison haute",
    HCH: "heures creuses saison haute",
    HPB: "heures pleines saison basse",
    HCB: "heures creuses saison basse",
    HPE: "heures pleines saison basse",
    HCE: "heures creuses saison basse",
    PTE: "heures de pointe",
    P: "heures de pointe",
    // LA MÊME CLASSE PORTE DEUX CODES SUR LE MÊME COMPTEUR (relevé le 16/09
    // sur le C2 `6a8eebfa`) : ses index et ses durées de dépassement la
    // nomment `P`, ses périodes de facturation `Pointe`. Sans cette entrée, la
    // carte écrivait « classe POINTE » au client.
    POINTE: "heures de pointe",
    PM: "heures de pointe mobile",
    JA: "jours de pointe",
}

/**
 * LES CODES QUI NOMMENT LA MÊME CLASSE (lot 5d4).
 *
 * Le même compteur nomme sa pointe `P` dans ses index et ses durées de
 * dépassement, `Pointe` dans ses périodes de facturation (relevé le 16/09 sur
 * le C2 `6a8eebfa`) ; le référentiel écrit aussi `PTE`. De même, la saison
 * basse s'écrit `HPE`/`HCE` ici et `HPB`/`HCB` là.
 *
 * Sans canonisation, la page demandait bien les deux noms — ce qu'il faut
 * faire, on ne devine pas lequel ce compteur valorise — mais les rendait en
 * DEUX lignes portant le même libellé client : « heures de pointe » deux fois,
 * dont une vide. Deux lignes pour une classe, c'est une classe inventée.
 *
 * Le code canonique ne s'écrit JAMAIS à l'écran : il ne sert qu'à reconnaître
 * que deux noms servis parlent de la même chose.
 */
const CLASSES_SYNONYMES: Record<string, string> = {
    P: "POINTE",
    PTE: "POINTE",
    POINTE: "POINTE",
    HPE: "HPB",
    HPB: "HPB",
    HCE: "HCB",
    HCB: "HCB",
}

/** Le code canonique d'une classe. Inchangé, en capitales, quand il est seul. */
export function classeCanonique(code: string): string {
    const majuscule = String(code ?? "").toUpperCase()
    return CLASSES_SYNONYMES[majuscule] ?? majuscule
}

/** Les grilles de découpage. Celle de l'acheminement est celle par défaut. */
const LIBELLES_GRILLE: Record<string, string> = {
    turpe: "",
    fournisseur: " au calendrier fournisseur",
}

const GRILLES = Object.keys(LIBELLES_GRILLE)

/**
 * Le suffixe d'une variable, découpé en grille et classe.
 *
 * Un suffixe qui s'arrête sur la GRILLE rend une classe VIDE : le nom nu
 * `energieactive_elecsoutirage_turpe` existe en base à côté de `..._turpeHPH`
 * (vocabulaire dapi, vérifié le 10/09). Il ne nomme aucune classe horaire, et
 * rien ne permet de deviner laquelle.
 */
export function decouperClasse(suffixe: string): {
    grille: string | null
    classe: string
} {
    for (const grille of GRILLES) {
        if (suffixe.startsWith(grille)) {
            return { grille, classe: suffixe.slice(grille.length) }
        }
    }
    return { grille: null, classe: suffixe }
}

/**
 * Ce qui s'écrit après la grandeur : « heures pleines saison haute ».
 *
 * Vide quand le suffixe ne nomme AUCUNE classe : « classe TURPE » inventerait
 * une classe horaire à partir du nom de notre grille tarifaire, et l'écrirait
 * sur une surface client. Les noms sans classe ne sont pas montrés du tout
 * (`regleDuNom`) ; ce retour vide est la seconde garde.
 */
export function libelleDeLaClasse(suffixe: string): string {
    const { grille, classe } = decouperClasse(suffixe)
    if (!classe) return ""
    const canonique = classeCanonique(classe)
    const mot = LIBELLES_CLASSE[canonique] ?? `classe ${canonique}`
    return `${mot}${grille ? LIBELLES_GRILLE[grille] : ""}`
}

// ============================================================================
// LA CARTE : d'un nom dataV2 servi à sa place dans la page
// ============================================================================

/**
 * Une règle de rangement. La PREMIÈRE qui reconnaît le nom gagne : l'ordre va du
 * plus spécifique au plus général.
 *
 * `classe` dit que le nom porte une classe temporelle après le préfixe ; le
 * libellé la reprend alors en toutes lettres.
 */
type RegleRangement = {
    onglet: CleOnglet
    /** Préfixe du nom dataV2, direction à substituer par `<sens>`. */
    prefixe: string
    /** Vrai quand ce qui suit le préfixe est une classe temporelle. */
    classe: boolean
    /** Ce qui s'écrit à l'écran, avant la classe. */
    libelle: string
    /** Clé stable de la série, avant la classe. */
    cle: string
    /** `null` quand l'unité dépend de la source et se lit dans la réponse. */
    unite: string | null
    origine: OrigineSerie
}

const REGLES: RegleRangement[] = [
    // --- Index (cumulés relevés par le compteur) ----------------------------
    {
        onglet: "index",
        prefixe: "idxenergieactive_elec<sens>_",
        classe: true,
        libelle: "Index",
        cle: "index",
        unite: "kWh",
        origine: "mesure",
    },
    // --- Dépassements de puissance -----------------------------------------
    {
        onglet: "depassements",
        prefixe: "duree_depassementpuissanceelec<sens>_",
        classe: true,
        libelle: "Durée de dépassement",
        cle: "depassement-duree",
        unite: "s",
        origine: "calcul",
    },
    {
        onglet: "depassements",
        prefixe: "energie_depassementpuissanceelec<sens>_",
        classe: true,
        libelle: "Énergie de dépassement",
        cle: "depassement-energie",
        unite: "kWh",
        origine: "calcul",
    },
    // --- Puissance ----------------------------------------------------------
    {
        onglet: "puissance",
        prefixe: "puissanceactive_elec<sens>_tout",
        classe: false,
        libelle: "Puissance active",
        cle: "puissance-active",
        // kW au pas fin ; au jour, la valeur peut être la puissance maximale
        // relevée (kVA) : l'unité se lit dans la réponse, elle ne se devine pas.
        unite: null,
        origine: "mesure",
    },
    {
        onglet: "puissance",
        prefixe: "puissancereactiveinductive_elec<sens>_tout",
        classe: false,
        libelle: "Puissance réactive inductive",
        cle: "reactif-inductif",
        unite: "kVAr",
        origine: "mesure",
    },
    {
        onglet: "puissance",
        prefixe: "puissancereactivecapacitive_elec<sens>_tout",
        classe: false,
        libelle: "Puissance réactive capacitive",
        cle: "reactif-capacitif",
        unite: "kVAr",
        origine: "mesure",
    },
    {
        onglet: "puissance",
        prefixe: "puissancereactive_elec<sens>_total",
        classe: false,
        libelle: "Puissance réactive totale",
        cle: "reactif-total",
        unite: "kVAr",
        // Nom de commodité du lecteur : somme de l'inductif et du capacitif au
        // même pas. Un calcul, jamais une feuille relevée telle quelle.
        origine: "calcul",
    },
    {
        onglet: "puissance",
        prefixe: "puissancereactive_elec<sens>_tout",
        classe: false,
        libelle: "Puissance réactive totale",
        cle: "reactif-somme",
        unite: "kVAr",
        // La somme déjà écrite par le distributeur sur les jours migrés : elle
        // ne coexiste jamais avec les composantes le même jour.
        origine: "mesure",
    },
    {
        onglet: "puissance",
        prefixe: "puissanceapparente_elec<sens>_tout",
        classe: false,
        libelle: "Puissance apparente",
        cle: "apparente",
        unite: "kVA",
        // Dérivée point à point, √(P² + Q²).
        origine: "calcul",
    },
    // --- Énergie ------------------------------------------------------------
    {
        onglet: "energie",
        prefixe: "energieactive_elec<sens>_tout",
        classe: false,
        libelle: "Énergie active",
        cle: "energie",
        unite: "kWh",
        origine: "mesure",
    },
    {
        onglet: "energie",
        prefixe: "energiereactive_elec<sens>_tout",
        classe: false,
        libelle: "Énergie réactive",
        cle: "energie-reactive",
        // Servie en kWh par le lecteur ; l'unité de la réponse fait foi.
        unite: null,
        origine: "mesure",
    },
    // --- Facturation (les périodes, énergie par classe) ---------------------
    {
        onglet: "facturation",
        prefixe: "energieactive_elec<sens>_",
        classe: true,
        libelle: "Énergie facturée",
        cle: "facturation",
        unite: "kWh",
        origine: "mesure",
    },
]

/** Le préfixe d'une règle, direction substituée. */
function prefixeDe(regle: RegleRangement, direction: DirectionEnedis): string {
    return regle.prefixe.replace("<sens>", direction)
}

/**
 * La règle qui range ce nom, ou `null` quand aucune ne le reconnaît.
 *
 * Un nom non reconnu n'est PAS montré : l'écrire demanderait un libellé qu'on
 * n'a pas, et un nom dataV2 brut sur une surface client est interdit (E9). La
 * garde de conformité E1 (`ongletsEnedis.spec.ts`) échoue alors sur les témoins,
 * ce qui est exactement le signal attendu : un nom neuf se range à la main, il
 * ne se devine pas.
 */
export function regleDuNom(
    variable: string,
    direction: DirectionEnedis
): RegleRangement | null {
    for (const regle of REGLES) {
        const prefixe = prefixeDe(regle, direction)
        if (!variable.startsWith(prefixe)) continue
        if (!regle.classe && variable !== prefixe) continue
        // Une règle à classe exige une CLASSE, pas seulement quelque chose après
        // le préfixe : le nom nu `…_turpe` s'arrête sur la grille et ne dit pas
        // de quelle classe horaire il parle. Le montrer écrirait une classe
        // inventée ; le taire ne perd aucune donnée nommée.
        if (
            regle.classe &&
            !decouperClasse(variable.slice(prefixe.length)).classe
        )
            continue
        return regle
    }
    return null
}

// ============================================================================
// DES VARIABLES SERVIES AUX ONGLETS
// ============================================================================

/** Les statistiques servies, filtrées sur le vocabulaire du lecteur. */
const STATS_CONNUES: StatSeries[] = ["mean", "min", "max", "sum", "last"]
const GRAINS_CONNUS: GrainSeries[] = [
    "natif",
    "hourly",
    "daily",
    "weekly",
    "monthly",
    "periode",
]

function statsServies(entree: VariableDataV2Servie): StatSeries[] {
    return STATS_CONNUES.filter((s) => (entree.stats ?? []).includes(s))
}

/**
 * Les bornes servies, grain par grain, recopiées sans rien compléter. Un grain
 * que la route ne liste pas n'a pas de bornes ici : il n'en a pas du tout.
 */
function bornesServies(
    entree: VariableDataV2Servie
): Partial<Record<GrainSeries, BornesVariableDataV2>> {
    const bornes: Partial<Record<GrainSeries, BornesVariableDataV2>> = {}
    for (const grain of GRAINS_CONNUS) {
        const servie = (entree.grains ?? {})[grain]
        if (servie) bornes[grain] = servie
    }
    return bornes
}

function grainsServis(entree: VariableDataV2Servie): GrainSeries[] {
    const servis = Object.keys(entree.grains ?? {})
    return GRAINS_CONNUS.filter((g) => servis.includes(g))
}

/**
 * Les classes temporelles que CE compteur porte, lues des variables qui les
 * nomment verbatim (index et périodes de facturation).
 *
 * Elles servent à compléter une famille dont la borne de disponibilité ne nomme
 * pas la classe (`classes_variables`, cas de la durée de dépassement, que dataV2
 * stocke par classe). Fabriquer les douze classes du référentiel remplirait la
 * page de séries vides ; se limiter à celles que le compteur nomme ailleurs ne
 * demande que des noms que ce compteur a déjà démontrés.
 */
export function classesDuCompteur(
    servies: VariableDataV2Servie[],
    direction: DirectionEnedis,
    grille: string
): string[] {
    const classes = new Set<string>()
    const prefixes = [
        `idxenergieactive_elec${direction}_${grille}`,
        `energieactive_elec${direction}_${grille}`,
    ]
    for (const entree of servies) {
        if (entree.classes_variables) continue
        for (const prefixe of prefixes) {
            if (
                entree.variable.startsWith(prefixe) &&
                entree.variable.length > prefixe.length
            ) {
                classes.add(entree.variable.slice(prefixe.length))
            }
        }
    }
    return [...classes].sort()
}

/** Les noms réellement demandables pour une entrée servie. */
function nomsDeLEntree(
    entree: VariableDataV2Servie,
    servies: VariableDataV2Servie[],
    direction: DirectionEnedis
): string[] {
    if (!entree.classes_variables) return [entree.variable]
    // La famille se termine par sa GRILLE (`…_turpe`) : les classes se lisent
    // sur les variables de la même grille que ce compteur nomme verbatim.
    const dernier = entree.variable.slice(entree.variable.lastIndexOf("_") + 1)
    const grille = GRILLES.includes(dernier) ? dernier : ""
    return classesDuCompteur(servies, direction, grille).map(
        (classe) => `${entree.variable}${classe}`
    )
}

export type DemandeCarte = {
    /**
     * Les variables dataV2 réellement servies, PAR SENS. Sans elles, la page
     * n'ouvre aucun onglet : elle ne devine pas.
     */
    servies?: Partial<Record<DirectionEnedis, VariableDataV2Servie[]>> | null
    /** Les sens regardés, dans l'ordre d'affichage. */
    directions: DirectionEnedis[]
}

/**
 * Les onglets de ce compteur, dans l'ordre, chacun avec ses séries. Un onglet
 * sans série n'est pas rendu.
 */
export function ongletsParSegment(demande: DemandeCarte): OngletResolu[] {
    const parOnglet = new Map<CleOnglet, SerieOnglet[]>()

    for (const direction of demande.directions) {
        const servies = demande.servies?.[direction] ?? []
        for (const entree of servies) {
            const stats = statsServies(entree)
            const grains = grainsServis(entree)
            if (stats.length === 0 || grains.length === 0) continue
            for (const variable of nomsDeLEntree(entree, servies, direction)) {
                const regle = regleDuNom(variable, direction)
                if (!regle) continue
                const suffixe = variable.slice(
                    prefixeDe(regle, direction).length
                )
                const libelle = regle.classe
                    ? `${regle.libelle} ${libelleDeLaClasse(suffixe)}`
                    : regle.libelle
                const series = parOnglet.get(regle.onglet) ?? []
                series.push({
                    cle: `${regle.cle}${regle.classe ? `-${suffixe}` : ""}-${direction}`,
                    libelle,
                    libelleFamille: regle.libelle,
                    variable,
                    direction,
                    stats,
                    unite: regle.unite,
                    origine: regle.origine,
                    grains,
                    classe: regle.classe
                        ? classeCanonique(decouperClasse(suffixe).classe)
                        : null,
                    bornes: bornesServies(entree),
                })
                parOnglet.set(regle.onglet, series)
            }
        }
    }

    return ORDRE_ONGLETS.filter((cle) => (parOnglet.get(cle)?.length ?? 0) > 0)
        .map((cle) => {
            const series = parOnglet.get(cle) ?? []
            const directions = DIRECTIONS.filter((d) =>
                series.some((s) => s.direction === d)
            )
            // L'onglet ne parle d'injection que s'il n'a QUE de l'injection à
            // montrer. Mêlé aux deux sens, il garde son mot neutre et laisse
            // les titres des graphes nommer chaque sens.
            const injectionSeule =
                directions.length === 1 && directions[0] === "injection"
            return {
                cle,
                libelle: injectionSeule
                    ? LIBELLES_ONGLETS_INJECTION[cle]
                    : LIBELLES_ONGLETS[cle],
                libelleRappel: injectionSeule
                    ? LIBELLES_RAPPEL_ONGLET_INJECTION[cle]
                    : LIBELLES_RAPPEL_ONGLET[cle],
                directions,
                series,
                grains: GRAINS_CONNUS.filter((grain) =>
                    series.some((s) => s.grains.includes(grain))
                ),
            }
        })
        .filter((onglet) => onglet.series.length > 0)
}

/** Toutes les variables d'un onglet pour une direction, sans doublon. */
export function variablesDeLOnglet(
    onglet: OngletResolu,
    direction: DirectionEnedis
): string[] {
    return [
        ...new Set(
            onglet.series
                .filter((s) => s.direction === direction)
                .map((s) => s.variable)
        ),
    ]
}
